/**
 * ItineraryFlightCard — flight summary card for the itinerary view.
 *
 * Shows one segment block per leg with a layover pill rendered between
 * adjacent connected legs. No barcodes, no editable fields, no fare details
 * (those live on the operational FlightVoucher). Purely read-only.
 *
 * Layout rules:
 *   - Pure CSS-table / inline-block / grid — no `display: flex` anywhere
 *     (PDF render safety).
 *   - Light backgrounds only — white cards on a `#F8FAFC` page; brand
 *     tints capped at `${primary}0a`.
 *
 * Props:
 *   flight       voucher.flight object — same schema as FlightVoucherBody.
 *   company      { primaryColor, accentColor } — brand tokens.
 *   passengers   voucher.flight.passengers[] — used to filter the baggage
 *                pax-type rows so we only render Adult/Child/Infant rows
 *                when at least one passenger of that type exists.
 *
 * Layover formula (per spec):
 *   mins = (next.departureDateTime - this.arrivalDateTime) / 60000
 *   Render only when 0 < mins < 1440 (24h). Anything else implies a
 *   day-stop / open-jaw / data error and is hidden.
 */
import React, { useState } from 'react';
import { formatVoucherDate } from '../../lib/voucherDate';
import { formatAirportName } from '../../lib/flightRoute';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name) {
  const s = String(name || '').trim();
  if (!s) return 'AL';
  const parts = s.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

// Truthy-non-empty check tolerant of non-string values (the LLM-extracted
// baggage fields are usually strings but can occasionally be null/number).
// BaggageBlock used this before it was defined, which threw a ReferenceError
// and crashed the whole card (no error boundary) — blanking the itinerary.
function has(v) {
  if (v == null) return false;
  return typeof v === 'string' ? v.trim().length > 0 : true;
}

// Combine ISO date + 24h time into a JS Date. Returns null on any failure
// (missing input, bad format) so the caller can hide the layover pill
// instead of rendering NaN.
function parseSegmentInstant(dateStr, timeStr) {
  if (!dateStr) return null;
  const t = (timeStr && /^\d{1,2}:\d{2}$/.test(timeStr)) ? timeStr : '00:00';
  const iso = `${dateStr}T${t.length === 4 ? '0' + t : t}:00`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtLayoverLabel(mins, city, code) {
  if (mins <= 0 || mins >= 1440) return null;
  const hrs = Math.floor(mins / 60);
  const m = mins % 60;
  const parts = [];
  if (hrs > 0) parts.push(`${hrs} hr`);
  if (m > 0) parts.push(`${m} mins`);
  const dur = parts.join(' ');
  const where = city ? `${city}${code ? ` (${code})` : ''}` : (code || '');
  return `Plane change: ${dur} layover${where ? ` in ${where}` : ''}`;
}

// ---------------------------------------------------------------------------
// AirlineLogo — 32×32 image. onError → state flag → render initials avatar.
// Pulls from airhex.com keyed by IATA carrier code so the itinerary tab gets
// the same logo a freshly-rebranded voucher does. `code` overrides `url`
// (we always trust the IATA code over a possibly-stale stored URL).
// ---------------------------------------------------------------------------

export function AirlineLogo({ url, code, name, primary, testId, size = 32 }) {
  // Phase 16.2.1 — chain through multiple logo CDNs before falling back
  // to initials. Old behaviour was a single avs.io fetch; if THAT failed
  // (corporate network proxy, slow upstream), the user saw initials and
  // never knew there was a logo available elsewhere.
  const [attempt, setAttempt] = useState(0);
  const codeUpper = (code || '').trim().toUpperCase();
  // Stored URLs may exist from voucher rebrand. Honour them when not from
  // the legacy airhex CDN (which 403s without a paid key).
  const storedOk = url && !/airhex\.com/i.test(url);
  // Logo source chain in priority order. Each entry must be tried in
  // sequence on error before we render the initials fallback.
  const sources = [];
  if (storedOk) sources.push(url);
  if (codeUpper) {
    sources.push(`https://pics.avs.io/64/64/${codeUpper}.png`);
    sources.push(`https://images.kiwi.com/airlines/64/${codeUpper}.png`);
    sources.push(`https://daisycon.io/images/airline/?width=64&height=64&iata=${codeUpper}`);
  }
  const px = `${size}px`;
  const effectiveUrl = sources[attempt];
  if (!effectiveUrl) {
    return (
      <span
        data-testid={testId}
        style={{
          display: 'inline-block',
          width: px,
          height: px,
          background: primary,
          color: '#FFFFFF',
          textAlign: 'center',
          lineHeight: px,
          fontWeight: 700,
          fontSize: `${Math.round(size * 0.34)}px`,
          borderRadius: '4px',
          letterSpacing: '0.5px',
          verticalAlign: 'middle',
        }}
      >
        {getInitials(name)}
      </span>
    );
  }
  return (
    <img
      data-testid={testId}
      src={effectiveUrl}
      alt={name || 'Airline'}
      width={size}
      height={size}
      onError={() => setAttempt((n) => n + 1)}
      // Anonymous CORS so the <img> can be re-encoded into the PDF without
      // tainting the canvas when this card is captured by Playwright.
      crossOrigin="anonymous"
      referrerPolicy="no-referrer"
      style={{
        display: 'inline-block',
        width: px,
        height: px,
        objectFit: 'contain',
        verticalAlign: 'middle',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// BaggageBlock — pax-type-aware. Renders rows only for pax types present in
// passengers[]. Falls back to a single generic row when no pax-type fields
// are populated. Returns null when there's no baggage data at all.
// ---------------------------------------------------------------------------

function BaggageBlock({ seg, passengers, accent, testId }) {
  const types = new Set(
    (passengers || []).map((p) => String(p?.type || '').trim().toLowerCase()),
  );

  const paxRows = [];
  if (types.has('adult')) {
    paxRows.push({
      label: 'Adult',
      checkIn: seg.baggageAdult || '',
      cabin: seg.cabinBaggageAdult || '',
    });
  }
  if (types.has('child')) {
    paxRows.push({ label: 'Child', checkIn: seg.baggageChild || '', cabin: '' });
  }
  if (types.has('infant')) {
    paxRows.push({ label: 'Infant', checkIn: seg.baggageInfant || '', cabin: '' });
  }

  const hasPaxData = paxRows.some((r) => r.checkIn || r.cabin);

  // Issue 8 guard: if the generic baggageCabin literally equals the generic
  // baggageCheckIn, that's almost certainly the LLM hallucinating the cabin
  // allowance by copying the check-in value (cabin is realistically always
  // smaller). Hide the duplicated cabin column rather than print wrong data.
  const genericCabin =
    has(seg.baggageCabin) && seg.baggageCabin.trim() !== (seg.baggageCheckIn || '').trim()
      ? seg.baggageCabin
      : '';

  let displayRows;
  if (hasPaxData) {
    displayRows = paxRows;
  } else if (seg.baggageCheckIn || genericCabin) {
    displayRows = [
      { label: 'Baggage', checkIn: seg.baggageCheckIn || '', cabin: genericCabin },
    ];
  } else {
    return null;
  }

  const headerCellStyle = {
    display: 'table-cell',
    padding: '4px 8px 4px 0',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontSize: '10px',
    color: accent,
  };
  const bodyCellStyle = {
    display: 'table-cell',
    padding: '4px 8px 4px 0',
    color: '#334155',
    fontSize: '11px',
  };

  return (
    <div
      data-testid={testId}
      style={{
        display: 'table',
        width: '100%',
        marginTop: '12px',
        tableLayout: 'fixed',
      }}
    >
      <div style={{ display: 'table-row' }}>
        <div style={{ ...headerCellStyle, width: '20%' }}>Baggage</div>
        <div style={{ ...headerCellStyle, width: '50%' }}>Check In</div>
        <div style={{ ...headerCellStyle, width: '30%' }}>Cabin</div>
      </div>
      {displayRows.map((r, i) => (
        <div key={i} style={{ display: 'table-row' }}>
          <div style={{ ...bodyCellStyle, fontWeight: 600 }}>{r.label}</div>
          <div style={bodyCellStyle}>{r.checkIn || '—'}</div>
          <div style={bodyCellStyle}>{r.cabin || '—'}</div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SegmentBlock — one segment / leg.
// ---------------------------------------------------------------------------

function SegmentBlock({ seg, idx, primary, accent, passengers }) {
  const flightCode = [seg.airlineCode, seg.flightNumber].filter(Boolean).join(' ');
  const subline = [seg.aircraft, seg.operatedBy].filter(Boolean).join(' | ');

  return (
    <div
      data-testid={`itinerary-flight-segment-${idx}`}
      className="break-inside-avoid pdf-no-break"
      style={{
        display: 'block',
        padding: '8px 0',
        pageBreakInside: 'avoid',
      }}
    >
      {/* Header — logo + airline + (optional) aircraft / operated-by */}
      <div style={{ display: 'table', width: '100%', marginBottom: '14px', tableLayout: 'fixed' }}>
        <div style={{ display: 'table-cell', verticalAlign: 'middle', width: '44px', paddingRight: '12px' }}>
          <AirlineLogo
            url={seg.airlineLogoUrl}
            code={seg.airlineCode}
            name={seg.airline}
            primary={primary}
            testId={`itinerary-flight-logo-${idx}`}
          />
        </div>
        <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
            {seg.airline || '—'}
            {seg.airlineCode && (
              <span
                style={{
                  marginLeft: '6px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  color: accent,
                  fontWeight: 600,
                }}
              >
                {seg.airlineCode}
              </span>
            )}
          </div>
          {flightCode && (
            <div
              style={{
                fontSize: '13px',
                fontWeight: 700,
                color: '#111827',
                marginTop: '2px',
                fontFamily: 'monospace',
              }}
            >
              {flightCode}
            </div>
          )}
        </div>
        {subline && (
          <div
            style={{
              display: 'table-cell',
              verticalAlign: 'middle',
              textAlign: 'right',
              fontSize: '11px',
              color: accent,
            }}
          >
            {subline}
          </div>
        )}
      </div>

      {/* Route row — Departure | Duration | Arrival */}
      <div style={{ display: 'table', width: '100%', tableLayout: 'fixed' }}>
        {/* Departure */}
        <div style={{ display: 'table-cell', verticalAlign: 'top', width: '40%' }}>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>
            {seg.departureTime || '—'}
          </div>
          <div style={{ fontSize: '11px', color: accent, marginTop: '4px' }}>
            {formatVoucherDate(seg.departureDate) || ''}
          </div>
          <div style={{ fontSize: '12px', color: '#334155', marginTop: '4px', fontWeight: 600 }}>
            {formatAirportName(seg.departureAirport || seg.departureCity)}
          </div>
          {seg.departureAirportCode && (
            <div style={{ fontSize: '11px', color: accent, marginTop: '2px', fontFamily: 'monospace' }}>
              {seg.departureAirportCode}
            </div>
          )}
          {seg.departureTerminal && (
            <div style={{ fontSize: '11px', color: accent, marginTop: '2px' }}>
              Terminal {seg.departureTerminal}
            </div>
          )}
        </div>

        {/* Duration centre rail */}
        <div style={{ display: 'table-cell', verticalAlign: 'middle', width: '20%', textAlign: 'center' }}>
          {seg.duration && (
            <div style={{ fontSize: '11px', color: accent, fontWeight: 600, marginBottom: '6px' }}>
              {seg.duration}
            </div>
          )}
          <div style={{ borderTop: '1px solid #E5E7EB', height: '1px', margin: '0 8px' }} />
          {Number.isInteger(seg.stops) && seg.stops > 0 && (
            <div style={{ fontSize: '10px', color: accent, marginTop: '4px' }}>
              {seg.stops} stop{seg.stops === 1 ? '' : 's'}
            </div>
          )}
        </div>

        {/* Arrival */}
        <div style={{ display: 'table-cell', verticalAlign: 'top', width: '40%', textAlign: 'right' }}>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>
            {seg.arrivalTime || '—'}
          </div>
          <div style={{ fontSize: '11px', color: accent, marginTop: '4px' }}>
            {formatVoucherDate(seg.arrivalDate) || ''}
          </div>
          <div style={{ fontSize: '12px', color: '#334155', marginTop: '4px', fontWeight: 600 }}>
            {formatAirportName(seg.arrivalAirport || seg.arrivalCity)}
          </div>
          {seg.arrivalAirportCode && (
            <div style={{ fontSize: '11px', color: accent, marginTop: '2px', fontFamily: 'monospace' }}>
              {seg.arrivalAirportCode}
            </div>
          )}
          {seg.arrivalTerminal && (
            <div style={{ fontSize: '11px', color: accent, marginTop: '2px' }}>
              Terminal {seg.arrivalTerminal}
            </div>
          )}
        </div>
      </div>

      <BaggageBlock
        seg={seg}
        passengers={passengers}
        accent={accent}
        testId={`itinerary-flight-baggage-${idx}`}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// LayoverPill — horizontal rule with centred pill. Rendered between
// consecutive segments when 0 < layoverMins < 24h.
// ---------------------------------------------------------------------------

function LayoverPill({ idx, label, accent }) {
  return (
    <div
      data-testid={`itinerary-flight-layover-${idx}`}
      style={{
        display: 'table',
        width: '100%',
        marginBottom: '10px',
        tableLayout: 'fixed',
      }}
    >
      <div style={{ display: 'table-cell', verticalAlign: 'middle', borderTop: '1px solid #E5E7EB' }} />
      <div
        style={{
          display: 'table-cell',
          verticalAlign: 'middle',
          width: '1%',
          whiteSpace: 'nowrap',
          padding: '0 12px',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            padding: '4px 14px',
            background: '#F8FAFC',
            border: '1px solid #E5E7EB',
            borderRadius: '20px',
            fontSize: '11px',
            color: accent,
            fontWeight: 600,
          }}
        >
          {label}
        </span>
      </div>
      <div style={{ display: 'table-cell', verticalAlign: 'middle', borderTop: '1px solid #E5E7EB' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ItineraryFlightCard({ flight, company, passengers }) {
  const primary = company?.primaryColor || '#34D0CB';
  const accent = company?.accentColor || '#595A5B';
  const segments = Array.isArray(flight?.segments) ? flight.segments : [];
  const paxList = Array.isArray(passengers) ? passengers : [];

  if (segments.length === 0) return null;

  return (
    <div data-testid="itinerary-flight-card" style={{ display: 'block' }}>
      {segments.map((seg, i) => {
        const isLast = i === segments.length - 1;
        let layoverLabel = null;
        if (!isLast) {
          const next = segments[i + 1];
          const arr = parseSegmentInstant(seg.arrivalDate, seg.arrivalTime);
          const dep = parseSegmentInstant(next?.departureDate, next?.departureTime);
          if (arr && dep) {
            const mins = Math.round((dep.getTime() - arr.getTime()) / 60000);
            layoverLabel = fmtLayoverLabel(mins, seg.arrivalCity, seg.arrivalAirportCode);
          }
        }
        return (
          <React.Fragment key={i}>
            <SegmentBlock
              seg={seg}
              idx={i}
              primary={primary}
              accent={accent}
              passengers={paxList}
            />
            {/* Visual separation between segments. The LayoverPill already
                renders horizontal rules either side of its centred pill, so
                when it's present it acts as the divider — no extra <hr>.
                Otherwise (no layover info, or last segment) we use a plain
                hairline divider. The last segment gets nothing. */}
            {!isLast && (
              layoverLabel ? (
                <LayoverPill idx={i} label={layoverLabel} accent={accent} />
              ) : (
                <hr
                  data-testid={`itinerary-flight-divider-${i}`}
                  style={{
                    border: 'none',
                    borderTop: '1px solid #F3F4F6',
                    margin: '12px 0',
                  }}
                  aria-hidden="true"
                />
              )
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
